// DÉCOUPER UNE TRANSCRIPTION — l'étape « Découper » du parcours d'une étude.
//
// On relit l'entretien tour de parole par tour de parole, et l'on SURLIGNE
// soi-même les passages qui feront preuve : chacun devient un verbatim, avec le
// locuteur et le timecode de son tour de parole. L'IA ne fait que SUGGÉRER
// (soulignés en pointillés) : chaque suggestion se retient ou s'ignore.
//
// Un verbatim est une CITATION : le serveur refuse un passage qui ne figure pas
// mot pour mot dans le tour de parole. On ne surligne donc qu'à l'intérieur
// d'un seul tour.
import { useMemo, useRef, useState, type MouseEvent as RMouseEvent } from 'react';
import {
  useCutVerbatim, useDepositStudy, useSource, useSuggestPassages, useUncutVerbatim,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { PassageSuggestion, Sentiment, SourceDetail } from '../api/types';
import { Button, ErrorBanner, Eyebrow, Note, Spinner } from '../components/ui';
import { Icon } from '../components/icons';
import { Link, useRouter } from '../router';
import { useCanWrite } from '../write/useCanWrite';
import './sources.css';

const SENTIMENTS: { value: Sentiment; label: string }[] = [
  { value: 'neg', label: 'Négatif' },
  { value: 'neu', label: 'Neutre' },
  { value: 'pos', label: 'Positif' },
];

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

function timecode(ms: number | null): string | null {
  if (ms === null || !Number.isFinite(ms)) return null;
  const t = Math.floor(ms / 1000);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return `${h ? `${h}:${String(m).padStart(2, '0')}` : m}:${String(s).padStart(2, '0')}`;
}

type Mark = { start: number; end: number; kind: 'kept' | 'suggested'; id: string };

/** Où se trouve un passage dans un tour de parole (casse ignorée), ou `null`. */
function locate(segment: string, passage: string): { start: number; end: number } | null {
  const p = passage.replace(/\s+/g, ' ').trim().toLowerCase();
  if (!p) return null;
  const i = segment.toLowerCase().indexOf(p);
  return i === -1 ? null : { start: i, end: i + p.length };
}

/** Le texte d'un tour de parole, découpé en morceaux surlignés ou non. */
function pieces(text: string, marks: Mark[]) {
  const sorted = [...marks].sort((a, b) => a.start - b.start);
  const out: { text: string; mark?: Mark }[] = [];
  let at = 0;
  for (const m of sorted) {
    if (m.start < at) continue; // chevauchement : le premier l'emporte
    if (m.start > at) out.push({ text: text.slice(at, m.start) });
    out.push({ text: text.slice(m.start, m.end), mark: m });
    at = m.end;
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out;
}

interface Pending { segment: number; text: string; x: number; y: number }

export function SourceCutScreen({ projectId, studyId, sourceId }: { projectId: string; studyId: string; sourceId: string }) {
  const query = useSource(sourceId);
  const canWrite = useCanWrite(projectId);
  const cut = useCutVerbatim(projectId, sourceId);
  const uncut = useUncutVerbatim(projectId, sourceId);
  const suggest = useSuggestPassages(sourceId);
  const deposit = useDepositStudy(projectId);
  const { navigate } = useRouter();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<PassageSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);

  const data: SourceDetail | undefined = query.data;
  const segments = useMemo(() => data?.source.segments ?? [], [data]);
  const kept = useMemo(() => data?.verbatims ?? [], [data]);

  // Les surlignages de chaque tour de parole : retenus (pleins), suggérés (pointillés).
  const marksBySegment = useMemo(() => {
    const map = new Map<number, Mark[]>();
    const add = (i: number, m: Mark) => map.set(i, [...(map.get(i) ?? []), m]);
    for (const v of kept) {
      const i = segments.findIndex((s) => locate(s.text, v.text));
      if (i !== -1) add(i, { ...locate(segments[i]!.text, v.text)!, kind: 'kept', id: v.id });
    }
    for (const s of suggestions) {
      const seg = segments[s.segment];
      const at = seg ? locate(seg.text, s.text) : null;
      if (at) add(s.segment, { ...at, kind: 'suggested', id: s.key });
    }
    return map;
  }, [kept, suggestions, segments]);

  // Surligner à la souris : un passage DANS un seul tour de parole.
  const onMouseUp = (e: RMouseEvent) => {
    if (!canWrite) return;
    if ((e.target as HTMLElement).closest('.cut-toolbar')) return;
    const sel = window.getSelection();
    const text = sel?.toString().replace(/\s+/g, ' ').trim() ?? '';
    if (!sel || sel.isCollapsed || text.length < 3) { setPending(null); return; }
    const segOf = (n: Node | null) => (n instanceof Element ? n : n?.parentElement)?.closest<HTMLElement>('[data-seg]');
    const a = segOf(sel.anchorNode); const b = segOf(sel.focusNode);
    if (!a || !b || a !== b) {
      setPending(null);
      setHint('Surlignez un passage à l’intérieur d’un seul tour de parole.');
      return;
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    const box = surfaceRef.current?.getBoundingClientRect();
    setHint(null);
    setPending({
      segment: Number(a.dataset['seg']), text,
      x: rect.left - (box?.left ?? 0) + rect.width / 2, y: rect.bottom - (box?.top ?? 0) + 6,
    });
  };

  const keep = (segment: number, text: string, sentiment: Sentiment, extra?: { gravity?: string; tags?: string[] }) => {
    setError(null);
    cut.mutate({ segment, text, sentiment, ...extra }, {
      onSuccess: () => { setPending(null); window.getSelection()?.removeAllRanges(); },
      onError: (e) => setError(errorMessage(e)),
    });
  };

  const keepSuggestion = (s: PassageSuggestion) => {
    keep(s.segment, s.text, s.sentiment, { gravity: s.gravity, tags: s.tags });
    setSuggestions((l) => l.filter((x) => x.key !== s.key));
  };

  const askSuggestions = () => {
    setError(null);
    suggest.mutate(undefined, { onSuccess: (r) => setSuggestions(r.suggestions), onError: (e) => setError(errorMessage(e)) });
  };

  const openAtelier = async () => {
    setError(null);
    try {
      if (canWrite) await deposit.mutateAsync(studyId);
      navigate(`/projet/${encodeURIComponent(projectId)}/atelier/${encodeURIComponent(studyId)}`);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const studyPath = `/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(studyId)}`;

  if (query.isPending) return <main className="screen"><Spinner label="Chargement de la transcription…" /></main>;
  if (query.isError || !data) {
    return (
      <main className="screen">
        <Link to={studyPath} className="zy-retour">← Étude</Link>
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
      </main>
    );
  }

  return (
    <main className="screen cut-screen">
      <Link to={studyPath} className="zy-retour">← Étude</Link>
      <div className="zy-page-head__texte">
        <Eyebrow>Découper la transcription</Eyebrow>
        <h1 className="t-h1">{data.source.originLabel ?? 'Transcription'}</h1>
        <p className="zy-intro">
          {canWrite
            ? 'Surlignez un passage avec la souris, puis choisissez son ton : il devient un verbatim, avec le locuteur et le moment où il a été dit.'
            : 'Les passages surlignés sont les verbatims retenus dans cette transcription.'}
        </p>
        <div className="screen-actions">
          {canWrite ? (
            <Button variant="ghost" onClick={askSuggestions} loading={suggest.isPending} disabled={segments.length === 0}>
              <Icon name="sparkles" size={14} /> {suggestions.length ? 'Suggérer à nouveau' : 'Suggérer des passages'}
            </Button>
          ) : null}
          <Button variant="accent" onClick={() => void openAtelier()} loading={deposit.isPending} disabled={kept.length === 0}>
            Traiter dans l’atelier
          </Button>
        </div>
      </div>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}

      <div className="cut-layout">
        <div className="cut-transcript" ref={surfaceRef} onMouseUp={onMouseUp}>
          {segments.length === 0 ? (
            <p className="zy-texte-muet">Le texte de cette transcription n’a pas été gardé : elle a été importée avant que le découpage existe.</p>
          ) : (
            <ol className="cut-segments">
              {segments.map((s, i) => (
                <li key={i} className="cut-segment">
                  <div className="cut-meta">
                    {timecode(s.tsMs) ? <span className="t-mono">{timecode(s.tsMs)}</span> : null}
                    {s.speaker ? <span className="cut-speaker">{s.speaker}</span> : null}
                  </div>
                  <p className="cut-text" data-seg={i}>
                    {pieces(s.text, marksBySegment.get(i) ?? []).map((p, k) =>
                      p.mark ? (
                        <mark key={k} className={p.mark.kind === 'kept' ? 'cut-kept' : 'cut-suggested'}>{p.text}</mark>
                      ) : (
                        <span key={k}>{p.text}</span>
                      ))}
                  </p>
                </li>
              ))}
            </ol>
          )}
          {pending ? (
            <div className="cut-toolbar" style={{ left: pending.x, top: pending.y }} role="toolbar" aria-label="Retenir le passage">
              <span className="cut-toolbar-label">Retenir comme</span>
              {SENTIMENTS.map((s) => (
                <button key={s.value} type="button" className={`cut-tone cut-tone-${s.value}`} disabled={cut.isPending}
                  onClick={() => keep(pending.segment, pending.text, s.value)}>
                  {s.label}
                </button>
              ))}
              <button type="button" className="cut-tone" aria-label="Annuler" onClick={() => { setPending(null); window.getSelection()?.removeAllRanges(); }}>×</button>
            </div>
          ) : null}
        </div>

        <aside className="cut-side">
          {hint ? <Note tone="avertissement">{hint}</Note> : null}

          {suggestions.length > 0 || suggest.data ? (
            <section className="cut-block">
              <h2 className="cut-h">Suggestions de l’IA {suggest.data?.mode === 'fake' ? <span className="cut-mode">démonstration</span> : null}</h2>
              {suggestions.length === 0 ? <p className="zy-texte-muet">Plus rien à suggérer.</p> : (
                <ul className="cut-list">
                  {suggestions.map((s) => (
                    <li key={s.key} className={`cut-item cut-item-${s.sentiment}`}>
                      <p>« {s.text} »</p>
                      <p className="cut-item-meta">
                        {[segments[s.segment]?.speaker, timecode(segments[s.segment]?.tsMs ?? null)].filter(Boolean).join(' · ')}
                      </p>
                      <div className="cut-item-actions">
                        <Button variant="primary" onClick={() => keepSuggestion(s)} disabled={cut.isPending}>Retenir</Button>
                        <Button variant="ghost" onClick={() => setSuggestions((l) => l.filter((x) => x.key !== s.key))}>Ignorer</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          <section className="cut-block">
            <h2 className="cut-h">Verbatims retenus <span className="t-mono">{kept.length}</span></h2>
            {kept.length === 0 ? (
              <p className="zy-texte-muet">Aucun pour l’instant.</p>
            ) : (
              <ul className="cut-list">
                {kept.map((v) => (
                  <li key={v.id} className={`cut-item cut-item-${v.sentiment ?? "neu"}`}>
                    <p>« {v.text} »</p>
                    <p className="cut-item-meta">{[v.persona_name ?? v.speaker, timecode(v.ts_ms)].filter(Boolean).join(' · ')}</p>
                    {canWrite ? (
                      <div className="cut-item-actions">
                        <button type="button" className="cut-link" disabled={uncut.isPending}
                          onClick={() => uncut.mutate(v.id, { onError: (e) => setError(errorMessage(e)) })}>
                          Retirer
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </main>
  );
}
